// pages/Home.tsx — public landing (spec §4.5). Hero wordmark animates v1→v2→v1.
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { Wordmark } from '../components/Wordmark';
import { faqJsonLd, softwareJsonLd, websiteJsonLd } from '../lib/seo';
import {
  ArrowRightIcon,
  CheckIcon,
  DocsIcon,
  GitIcon,
  KeyIcon,
  NotesIcon,
  SparkleIcon,
  VoiceIcon,
} from '../components/icons';

const FAQS = [
  {
    q: 'What is UPLOAD?',
    a: 'UPLOAD is a work digital twin. It ingests the things you produce — documents, notes, code and pull requests, think-aloud voice — and distills how you work into an owner-controlled memory your own AI agents can use to act as you.',
  },
  {
    q: 'Does it copy my content?',
    a: 'No. UPLOAD follows the Extraction Principle: it captures the method — your reasoning, patterns, principles, and an index of what you know — not a copy of the source material. Raw material stays private provenance.',
  },
  {
    q: 'How do agents use it?',
    a: 'Your memory is exposed over the Model Context Protocol (MCP). You mint scoped, revocable tokens so any MCP-capable agent (Claude, Copilot, Cursor, your own orchestrators) can recall how you think and contribute work back — as you.',
  },
  {
    q: 'Who can see my data?',
    a: 'Only you. Every row, blob, and key is scoped to a single owner, encrypted at rest, and reachable only through one authenticated gateway. Export or delete everything at any time.',
  },
];

const STEPS = [
  {
    n: '1',
    h: 'Ingest',
    p: 'Connect documents, notes, repositories, and think-aloud voice. Each source is stored as private provenance.',
    icon: DocsIcon,
  },
  {
    n: '2',
    h: 'Distill',
    p: 'The system extracts how you work — reasoning, execution steps, patterns, principles — and indexes what you know.',
    icon: SparkleIcon,
  },
  {
    n: '3',
    h: 'Recall & act',
    p: 'Your agents recall that distilled memory over MCP and contribute their work back, so the twin keeps learning.',
    icon: KeyIcon,
  },
];

const PILLARS = [
  { h: 'Thinking', p: 'How you reason, weigh trade-offs, and decide.', icon: SparkleIcon },
  { h: 'Execution', p: 'How you actually do the work, step by step.', icon: CheckIcon },
  {
    h: 'Patterns & Ethics',
    p: 'Your repeated approaches, standards, and red lines.',
    icon: GitIcon,
  },
  { h: 'Knowledge', p: 'What you know — indexed, never copied.', icon: DocsIcon },
];

const SOURCES = [
  { h: 'Documents', p: 'Text, Markdown, and HTML you authored.', icon: DocsIcon },
  { h: 'Notes', p: 'Free-form notes, refined then distilled.', icon: NotesIcon },
  { h: 'Code & PRs', p: 'Your commits and reviews from GitHub or Azure DevOps.', icon: GitIcon },
  { h: 'Voice', p: 'Think-aloud recordings and live conversation.', icon: VoiceIcon },
];

export function Home() {
  return (
    <>
      <Seo
        title="UPLOAD — Your working self, digitized"
        description="UPLOAD is a work digital twin. It captures how you think, execute, and what you know — then exposes that distilled memory to your own AI agents over MCP, so they can act as you. Private by default, encrypted, export or delete anytime."
        path="/"
        jsonLd={[softwareJsonLd, websiteJsonLd, faqJsonLd(FAQS)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-32 mx-auto h-80 max-w-4xl rounded-full bg-brand/20 blur-3xl"
        />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-6 py-20 text-center sm:py-28">
          <span className="badge badge-brand mb-6">Your working self, digitized</span>
          <h1>
            <Wordmark animated className="text-5xl sm:text-7xl" />
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-fg-muted sm:text-xl">
            A <strong className="text-fg">work digital twin</strong> that captures{' '}
            <strong className="text-fg">how</strong> you think, execute, and what you know — then
            lets your own AI agents recall it and <em>act as you</em>. Method and pattern, never a
            copy of your content.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/app" className="btn">
              Get Started
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link to="/about" className="btn-ghost">
              Learn more
            </Link>
          </div>
          <p className="mt-6 text-xs text-fg-subtle">
            Owner-controlled · Encrypted at rest · Export or delete anytime
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-5xl px-6 pb-8">
        <h2 className="text-center text-2xl font-semibold tracking-tight">How it works</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.h} className="card">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                    <Icon />
                  </div>
                  <span className="text-sm font-semibold text-fg-subtle">Step {s.n}</span>
                </div>
                <h3 className="mt-4 text-lg font-semibold">{s.h}</h3>
                <p className="mt-1.5 text-sm text-fg-muted">{s.p}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What it captures */}
      <section className="mx-auto grid max-w-4xl grid-cols-1 gap-4 px-6 py-12 sm:grid-cols-2">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.h}
              className="card transition-transform duration-200 hover:-translate-y-0.5"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <Icon />
              </div>
              <h2 className="mt-4 text-lg font-semibold">{p.h}</h2>
              <p className="mt-1.5 text-sm text-fg-muted">{p.p}</p>
            </div>
          );
        })}
      </section>

      {/* Sources */}
      <section className="mx-auto max-w-5xl px-6 py-8">
        <h2 className="text-center text-2xl font-semibold tracking-tight">Feed it your work</h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-fg-muted">
          Every source teaches the twin a little more about how you operate.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {SOURCES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.h} className="card text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <Icon />
                </div>
                <h3 className="mt-3 text-sm font-semibold">{s.h}</h3>
                <p className="mt-1 text-xs text-fg-muted">{s.p}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* For your agents (MCP) */}
      <section className="mx-auto max-w-4xl px-6 py-12">
        <div className="card bg-gradient-to-br from-surface to-surface-2">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <KeyIcon className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">
                Infrastructure for your agents
              </h2>
              <p className="mt-3 text-fg-muted">
                UPLOAD is the substrate that gives any number of agents your thinking material. Mint{' '}
                <strong className="text-fg">scoped, revocable tokens</strong> and connect any
                MCP-capable agent — Claude, Copilot, Cursor, or your own orchestrators — to{' '}
                <code className="rounded bg-surface-2 px-1.5 py-0.5 text-sm">recall</code>,{' '}
                <code className="rounded bg-surface-2 px-1.5 py-0.5 text-sm">how_i_think</code>, and{' '}
                <code className="rounded bg-surface-2 px-1.5 py-0.5 text-sm">contribute</code> back
                what they did as you. Read/write scopes, per-unit-type limits, and rate limits are
                enforced per agent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Extraction Principle */}
      <section className="mx-auto max-w-3xl px-6 pb-24">
        <div className="card bg-gradient-to-br from-surface to-surface-2 text-center">
          <h2 className="text-2xl font-semibold tracking-tight">The Extraction Principle</h2>
          <p className="mx-auto mt-4 max-w-2xl text-fg-muted">
            Every source extracts <strong className="text-fg">how</strong> you work — your
            reasoning, your approach, the patterns you repeat — and indexes what you know. Your raw
            material stays private provenance; your <em>persona</em> is owner-controlled,
            exportable, and deletable with one action.
          </p>
          <div className="mt-8">
            <Link to="/app" className="btn">
              Get Started
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
